import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { DocumentosListaTab } from '@/components/DocumentosListaTab';
import { PlantillaListaTab } from '@/components/PlantillaListaTab';
import { ExpedienteJeeTab } from '@/components/ExpedienteJeeTab';
import { labelNivel } from '@/lib/labels';

interface Candidato {
  id: number;
  id_onpe: string;
  dni: string | null;
  nombres_onpe: string | null;
  apellidos_onpe: string | null;
  cargo: string;
  orden: number | null;
  sexo: string | null;
  edad_al_cierre: number | null;
  es_designado: boolean;
  en_padron_arpp: boolean;
  desafiliado_jne: boolean;
  mismatch_territorial: boolean;
  estado_validacion: string;
}

interface Lista {
  id: number;
  n_lista: number;
  region: string;
  provincia: string | null;
  distrito: string | null;
  nivel: string;
  tipo_eleccion: string;
  alerta_maxima: string;
  estado_jee: string;
  cumple_paridad_horizontal: boolean | null;
  cumple_alternancia: boolean | null;
  cumple_simetria_acc_titular: boolean | null;
  cumple_cuota_joven: boolean | null;
  pct_designados: number | null;
}

function check(v: boolean | null) {
  if (v === null) return <span className="text-gray-400">—</span>;
  return v ? <span className="text-green-600">✓</span> : <span className="text-red-600">✗</span>;
}

type Tab = 'Composición' | 'Documentos' | 'Plantilla' | 'Expediente JEE';

export function ListaDetalle() {
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('Composición');

  const { data: lista } = useQuery<Lista>({
    queryKey: ['lista', id],
    queryFn: async () => (await api.get<Lista>(`/listas/${id}`)).data,
  });
  const { data: cands } = useQuery<Candidato[]>({
    queryKey: ['lista-cands', id],
    queryFn: async () => (await api.get<Candidato[]>(`/listas/${id}/candidatos`)).data,
    enabled: tab === 'Composición',
  });

  if (!lista) return <div className="p-6 text-sm text-gray-500">Cargando…</div>;

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <Link to="/listas" className="text-xs text-arpp-primary">← Volver a listas</Link>
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-arpp-primary">
            Lista N° {lista.n_lista}
          </h1>
          <p className="text-xs text-gray-600">
            {lista.region} · {lista.provincia ?? '—'} · {lista.distrito ?? '—'} · {lista.nivel} · {labelNivel(lista.tipo_eleccion)}
          </p>
        </div>
        <button
          onClick={async () => {
            const r = await api.get(`/listas/${id}/reporte.pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `auditoria_lista_${lista.n_lista}_${lista.region.replace(/ /g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }}
          className="bg-arpp-primary text-white px-3 py-2 rounded text-xs hover:bg-blue-800"
        >
          📄 Descargar reporte PDF
        </button>
      </header>

      <section className="bg-white border rounded p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
        <div><div className="text-gray-500">Paridad H+M (V01)</div><div>{check(lista.cumple_paridad_horizontal)}</div></div>
        <div><div className="text-gray-500">Alternancia M-F (V02-V04)</div><div>{check(lista.cumple_alternancia)}</div></div>
        <div><div className="text-gray-500">Simetría Acc-Tit (V05)</div><div>{check(lista.cumple_simetria_acc_titular)}</div></div>
        <div><div className="text-gray-500">Cuota joven 20% (V06)</div><div>{check(lista.cumple_cuota_joven)}</div></div>
        <div><div className="text-gray-500">% designados</div><div>{lista.pct_designados ?? '—'}</div></div>
      </section>

      <nav className="flex gap-1 border-b">
        {(['Composición', 'Documentos', 'Plantilla', 'Expediente JEE'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === t ? 'border-arpp-primary text-arpp-primary font-medium' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            {t === 'Documentos' ? '📄 Documentos de la Lista' :
             t === 'Plantilla' ? '📊 Plantilla XLSX' :
             t === 'Expediente JEE' ? '⚖️ Expediente JEE' : t}
          </button>
        ))}
      </nav>

      {tab === 'Documentos' && <DocumentosListaTab listaId={Number(id)} />}
      {tab === 'Plantilla' && <PlantillaListaTab listaId={Number(id)} />}
      {tab === 'Expediente JEE' && <ExpedienteJeeTab listaId={Number(id)} />}

      {tab === 'Composición' && (
      <div className="bg-white border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Orden</th>
              <th className="px-3 py-2 text-left">Cargo</th>
              <th className="px-3 py-2 text-left">DNI</th>
              <th className="px-3 py-2 text-left">Apellidos y nombres</th>
              <th className="px-3 py-2 text-left">Sexo</th>
              <th className="px-3 py-2 text-right">Edad</th>
              <th className="px-3 py-2 text-left">Padrón</th>
              <th className="px-3 py-2 text-left">Desaf.</th>
              <th className="px-3 py-2 text-left">Domicilio</th>
              <th className="px-3 py-2 text-left">Estado</th>
            </tr>
          </thead>
          <tbody>
            {cands?.map((c) => (
              <tr key={c.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-1.5">{c.orden ?? '—'}</td>
                <td className="px-3 py-1.5 text-xs">{c.cargo}</td>
                <td className="px-3 py-1.5 font-mono text-xs">
                  <Link to={`/candidatos/${c.id}`} className="text-arpp-primary hover:underline">
                    {c.es_designado && !c.dni ? <span className="text-amber-700">PENDIENTE</span> : c.dni}
                  </Link>
                </td>
                <td className="px-3 py-1.5">
                  <Link to={`/candidatos/${c.id}`} className="hover:underline">
                  {c.es_designado && !c.nombres_onpe ? <em className="text-gray-500">[DESIGNADO]</em> :
                    `${c.apellidos_onpe ?? ''}, ${c.nombres_onpe ?? ''}`}
                  </Link>
                </td>
                <td className="px-3 py-1.5">{c.sexo ?? '—'}</td>
                <td className="px-3 py-1.5 text-right">{c.edad_al_cierre ?? '—'}</td>
                <td className="px-3 py-1.5">{c.en_padron_arpp ? '✓' : '✗'}</td>
                <td className="px-3 py-1.5">{c.desafiliado_jne ? <span className="text-red-600">⚠</span> : '—'}</td>
                <td className="px-3 py-1.5">{c.mismatch_territorial ? <span className="text-amber-700">⚠</span> : '—'}</td>
                <td className="px-3 py-1.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${
                    c.estado_validacion === 'ok' ? 'bg-green-100 text-green-700' :
                    c.estado_validacion === 'bloqueante' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'}`}>
                    {c.estado_validacion}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
